import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { UserService } from '../user/user.service';
import { StoreContextService } from '../store-context/store-context.service';
import { IdleService } from './idle/idle.service';
import { IDLE_LOGOUT_NOTICE_KEY, isIdleExpired } from './idle/idle.constants';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/**
 * Authentication Initializer Service
 * Ensures proper authentication flow during app startup.
 * Also waits for store context to load so the header store selector shows on first paint.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInitializerService {
  private _authService = inject(AuthService);
  private _userService = inject(UserService);
  private _storeContext = inject(StoreContextService);
  private _idleService = inject(IdleService);

  /**
   * Initialize authentication state
   * This runs BEFORE any routes are loaded
   */
  initialize(): Observable<boolean> {
    this._authService.listenForPasswordResetLogout();
    this._idleService.start();

    const token = this._authService.accessToken;

    if (!token) {
      return of(true);
    }

    if (isIdleExpired()) {
      this._authService.clearLocalSession();
      sessionStorage.setItem(IDLE_LOGOUT_NOTICE_KEY, '1');
      return of(true);
    }

    try {
      const tokenPayload = this.parseJwt(token);
      const isExpired = tokenPayload.exp * 1000 < Date.now();

      if (isExpired) {
        this._authService.signOut().subscribe();
        return of(true);
      }

      // Wait for stores so header store dropdown shows on first load (no refresh needed)
      const waitForStores = () =>
        timer(0).pipe(
          switchMap(() => this._storeContext.whenStoresLoaded()),
          map(() => true),
          catchError(() => of(true))
        );

      const storedUser = this._userService.currentUser;
      if (storedUser && storedUser.id) {
        return waitForStores();
      }

      return this._userService.get(true).pipe(
        catchError((error) => {
          this._authService.signOut().subscribe();
          return of(null);
        }),
        switchMap(() => waitForStores())
      );
    } catch (error) {
      this._authService.signOut().subscribe();
      return of(true);
    }
  }

  /**
   * Parse JWT token to get payload
   */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return { exp: 0 };
    }
  }
}

